import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_BUDGETS,
  RECEIVING_BUDGETS,
  RECEIVE_BUDGET_METADATA,
  RECEIVE_BUDGET_FAILURE,
  SAVING_BUDGET,
  SAVING_BUDGET_FAILURE,
  UPDATING_BUDGET,
  UPDATING_BUDGET_FAILURE,
  DELETE_BUDGET,
  DELETING_BUDGET,
  DELETING_BUDGET_FAILURE,
  INCREMENT_BUDGET_COUNT,
  RESET_BUDGET_META,
  CLEAR_BUDGET_STATE,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const budgetSchema = new schema.Entity('budgets')
const budgetListSchema = new schema.Array(budgetSchema)

const state = {
  data: {},
  cache: {},
  lines: null,
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchBudgetsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    this.commit(RECEIVING_BUDGETS, true)
    return Vue.http.get(buildUrlWithQuery('budgets', query, instanceUrl), { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BUDGETS, false)
        this.commit(RESET_BUDGET_META)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, budgetListSchema)
          this.commit(RECEIVE_BUDGETS, { data: entities.budgets })
          this.commit(RECEIVE_BUDGET_METADATA, { next, previous, count, result: result.length })
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVING_BUDGETS, false)
        this.commit(RECEIVE_BUDGET_FAILURE, handleXHRError(e))
      })
  },
  fetchBudgetOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_BUDGETS, true)
    return Vue.http({ url: buildUrl('budgets', query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BUDGETS, false)
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities, result } = normalize(choices, budgetListSchema)
          this.commit(RECEIVE_BUDGETS, !result ? { data: choices } : { data: entities.budgets })
          return r
        }
      })
      .catch(e => {
        this.commit(RECEIVING_BUDGETS, false)
        this.commit(RECEIVE_BUDGET_FAILURE, handleXHRError(e))
      })
  },
  populateBudget (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.post(buildUrl(`budgets/${id}/populate`, null, instanceUrl), null, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r || (r && r.hasOwnProperty('ok') && !r.ok)) {
          return Promise.reject(`Could not refresh budgetlines for budget id #${id}`)
        }
        return r
      })
      .catch(e => {
        console.log(e)
        return e
      })
  },
  clearBudgetState (store, clearCache = false) {
    this.commit(CLEAR_BUDGET_STATE, clearCache)
  },
  importBudget (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.post(buildUrl(`import-budgetlines`, null, instanceUrl), payload, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r || (r && r.hasOwnProperty('ok') && !r.ok)) {
          return Promise.reject(`Could not import budget`)
        }
        return r
      })
      .catch(e => {
        console.log(e)
        return e
      })
  }
}

const getters = {
  getBudgets: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getBudgetMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getBudgetById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getBudgetFetchStatus: (state) => {
    return state.receivingData
  },
  getBudgetSaveStatus: (state) => {
    return state.saving
  },
  getBudgetUpdateStatus: (state) => {
    return state.updating
  },
  getBudgetDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_BUDGETS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_BUDGETS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_BUDGET_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_BUDGET] (state, payload) {
    state.saving = payload
  },
  [SAVING_BUDGET_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_BUDGET] (state, payload) {
    state.updating = payload
  },
  [UPDATING_BUDGET_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_BUDGET] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_BUDGET] (state, payload) {
    state.deleting = payload
  },
  [DELETING_BUDGET_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { id, err }
  },
  [INCREMENT_BUDGET_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_BUDGET_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [RESET_BUDGET_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_BUDGET_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/budget.js